import { ReactElement } from 'react';
import { Button } from 'react-bootstrap';
import { LiveInspectionResponse } from '../../../../data/entities';
import { Icon, IconDefinitions } from '../../../../components';

interface AgentLiveInspectionsBannerProps {
  inspections: LiveInspectionResponse[];
  onWatchLive: (inspection: LiveInspectionResponse) => void;
}

export function AgentLiveInspectionsBanner({ inspections, onWatchLive }: AgentLiveInspectionsBannerProps): ReactElement | null {
  if (inspections.length === 0) return null;

  // Single file
  if (inspections.length === 1) {
    const inspection = inspections[0];
    return (
      <div
        className="d-flex align-items-center justify-content-between gap-2 px-3 px-sm-4 py-2"
        style={{
          backgroundColor: 'rgba(39, 174, 96, 0.06)',
          borderBottom: '1px solid rgba(39, 174, 96, 0.2)',
          cursor: 'pointer',
        }}
        onClick={() => onWatchLive(inspection)}
      >
        <div className="d-flex align-items-center gap-2 min-w-0 overflow-hidden flex-grow-1">
          <span
            className="spinner-grow spinner-grow-sm bg-success flex-shrink-0"
            style={{ width: 7, height: 7 }}
          />
          <span className="fw-semibold text-success flex-shrink-0" style={{ fontSize: '0.8rem' }}>
            Live Inspection Available
          </span>
          <code className="text-muted text-truncate d-block" style={{ fontSize: '0.75rem' }}>
            {inspection.fileName}
          </code>
        </div>
        <Button
          variant="success"
          size="sm"
          onClick={(e) => { e.stopPropagation(); onWatchLive(inspection); }}
          className="d-flex align-items-center gap-1 rounded-pill flex-shrink-0"
          style={{ fontSize: '0.65rem' }}
        >
          <Icon iconName={IconDefinitions.CTEye} />
          Watch live
        </Button>
      </div>
    );
  }

  // Multiple files
  return (
    <div>
      <div
        className="d-flex align-items-center justify-content-between px-3 px-sm-4 py-2"
        style={{
          backgroundColor: 'rgba(39, 174, 96, 0.06)',
          borderBottom: '1px solid rgba(39, 174, 96, 0.2)',
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <span
            className="spinner-grow spinner-grow-sm bg-success flex-shrink-0"
            style={{ width: 7, height: 7 }}
          />
          <span className="fw-semibold text-success" style={{ fontSize: '0.8rem' }}>
            {inspections.length} Live Inspections Available
          </span>
        </div>
      </div>

      {inspections.map((inspection, index) => (
        <div
          key={inspection.filePath}
          className="d-flex align-items-center justify-content-between gap-2 px-3 px-sm-4 py-2"
          style={{
            borderBottom: index < inspections.length - 1
              ? '1px solid rgba(39, 174, 96, 0.12)'
              : '1px solid rgba(39, 174, 96, 0.2)',
            cursor: 'pointer',
            transition: 'background-color 0.1s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(39, 174, 96, 0.04)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          onClick={() => onWatchLive(inspection)}
        >
          <div className="d-flex align-items-center gap-2 min-w-0 overflow-hidden flex-grow-1">
            <span
              className="spinner-grow spinner-grow-sm bg-success flex-shrink-0"
              style={{ width: 6, height: 6 }}
            />
            <code className="text-muted text-truncate d-block" style={{ fontSize: '0.75rem' }}>
              {inspection.fileName}
            </code>
          </div>
          <Button
            variant="success"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onWatchLive(inspection); }}
            className="d-flex align-items-center gap-1 rounded-pill flex-shrink-0"
            style={{ fontSize: '0.65rem' }}
          >
            <Icon iconName={IconDefinitions.CTEye} />
            Watch live
          </Button>
        </div>
      ))}
    </div>
  );
}